import { Box, Typography, Chip, Stack } from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Resource, ResourceType } from '../../lib/resources';
import { getTagCategoryStyle } from '../../lib/tags';

const TYPE_CONFIG: Record<ResourceType, { label: string; color: string; icon: React.ReactNode; gradient: string }> = {
  blog: {
    label: 'Blog',
    color: '#3b82f6',
    icon: <ArticleOutlinedIcon sx={{ fontSize: 14 }} />,
    gradient: 'linear-gradient(135deg, #93c5fd 0%, #a78bfa 100%)',
  },
  research: {
    label: 'Research',
    color: '#8b5cf6',
    icon: <ScienceOutlinedIcon sx={{ fontSize: 14 }} />,
    gradient: 'linear-gradient(135deg, #c4b5fd 0%, #f9a8d4 100%)',
  },
  'case-study': {
    label: 'Case Study',
    color: '#10b981',
    icon: <DescriptionOutlinedIcon sx={{ fontSize: 14 }} />,
    gradient: 'linear-gradient(135deg, #6ee7b7 0%, #67e8f9 100%)',
  },
};

interface ResourceCardProps {
  resource: Resource;
}

export function ResourceCard({ resource }: ResourceCardProps) {
  const typeConfig = TYPE_CONFIG[resource.type];

  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'grey.400',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          transform: 'translateY(-3px)',
        },
      }}
    >
      {/* Image area with type chip */}
      <Box
        sx={{
          position: 'relative',
          height: 160,
          background: resource.image ? undefined : typeConfig.gradient,
          bgcolor: resource.image ? 'grey.100' : undefined,
        }}
      >
        {resource.image && (
          <Box
            component="img"
            src={resource.image}
            alt={resource.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
        <Chip
          icon={typeConfig.icon as React.ReactElement}
          label={typeConfig.label}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
            bgcolor: typeConfig.color,
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
            height: 24,
            '& .MuiChip-icon': { color: '#fff', ml: 0.5 },
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.35, color: 'text.primary' }}
        >
          {resource.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          {resource.date
            ? new Date(resource.date).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })
            : ''}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.5,
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            lineHeight: 1.45,
          }}
        >
          {resource.description}
        </Typography>

        {/* Tag chips */}
        {resource.tags && resource.tags.length > 0 && (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {resource.tags.slice(0, 3).map((tag) => {
              const style = getTagCategoryStyle(tag);
              return (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 22,
                    borderColor: style.border,
                    color: style.color,
                    fontWeight: 500,
                    cursor: 'default',
                  }}
                />
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
